import React, { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, ArrowsClockwise, FileArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CsvExport } from "@/components/CsvExport";

const METHODS = ["cash", "bank_transfer", "check", "card", "other"];
const TDS_SECTIONS = [
  { value: "none", label: "None (no TDS)", rate: 0 },
  { value: "194C", label: "194C — Works (1% Indv/HUF)", rate: 1 },
  { value: "194C_COMPANY", label: "194C — Works (2% Company)", rate: 2 },
  { value: "194J", label: "194J — Professional (10%)", rate: 10 },
  { value: "CUSTOM", label: "Custom section", rate: 0 },
];

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

export default function Payments() {
  const [rows, setRows] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    invoice_id: "", amount: 0, date: new Date().toISOString().slice(0, 10),
    method: "bank_transfer", reference: "", notes: "",
    tds_section: "none", tds_rate: 0, tds_certificate_no: "",
  });

  const load = useCallback(() => Promise.all([api.get("/payments"), api.get("/invoices")]).then(([a, b]) => { setRows(a.data); setInvoices(b.data); }), []);
  useEffect(() => { load(); }, [load]);

  const tdsAmount = Number(form.amount || 0) * Number(form.tds_rate || 0) / 100;
  const netAmount = Number(form.amount || 0) - tdsAmount;

  const submit = async () => {
    try {
      const section = form.tds_section === "none" ? "" : form.tds_section.replace("_COMPANY", "");
      await api.post("/payments", {
        ...form,
        amount: Number(form.amount),
        tds_section: section,
        tds_rate: Number(form.tds_rate || 0),
      });
      toast.success("Payment recorded");
      setOpen(false);
      setForm({
        invoice_id: "", amount: 0, date: new Date().toISOString().slice(0, 10),
        method: "bank_transfer", reference: "", notes: "",
        tds_section: "none", tds_rate: 0, tds_certificate_no: "",
      });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this payment?")) return;
    try { await api.delete(`/payments/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const invMap = Object.fromEntries(invoices.map((i) => [i.id, i]));

  return (
    <div data-testid="payments-page">
      <PageHeader
        title="Payments"
        subtitle="Record incoming payments against invoices."
        module="Sales · SD/AR"
        ribbon={
          <>
            <BcRibbonGroup label="New">
              <BcRibbonBtn icon={Plus} label="Record Payment" variant="primary" onClick={() => setOpen(true)} testid="ribbon-new-payment" />
            </BcRibbonGroup>
            <BcRibbonGroup label="Process">
              <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="ribbon-refresh" />
            </BcRibbonGroup>
            <BcRibbonGroup label="Report">
              <CsvExport resource="payments" render={({ onClick }) => (
                <BcRibbonBtn icon={FileArrowDown} label="Export CSV" onClick={onClick} testid="ribbon-export-csv" />
              )} />
            </BcRibbonGroup>
          </>
        }
      />
      <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent className="w-[440px]">
              <SheetHeader><SheetTitle className="font-display">Record Payment</SheetTitle></SheetHeader>
              <div className="space-y-4 mt-6">
                <Field label="Invoice">
                  <Select value={form.invoice_id} onValueChange={(v) => setForm({ ...form, invoice_id: v })}>
                    <SelectTrigger data-testid="payment-invoice-select"><SelectValue placeholder="Select invoice" /></SelectTrigger>
                    <SelectContent>
                      {invoices.map((i) => (
                        <SelectItem key={i.id} value={i.id}>
                          {i.invoice_number} — {i.client_name} · balance {fmtMoney(i.total - i.amount_paid)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Amount"><Input type="number" data-testid="payment-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
                  <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
                </div>
                <Field label="Method">
                  <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Reference"><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></Field>

                {/* TDS block */}
                <div className="border-t border-slate-200 pt-4 space-y-3">
                  <Field label="TDS section">
                    <Select
                      value={form.tds_section}
                      onValueChange={(v) => {
                        const preset = TDS_SECTIONS.find((s) => s.value === v);
                        setForm({ ...form, tds_section: v, tds_rate: preset && preset.rate ? preset.rate : (v === "CUSTOM" ? form.tds_rate : 0) });
                      }}
                    >
                      <SelectTrigger data-testid="payment-tds-section-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{TDS_SECTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  {form.tds_section !== "none" && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="TDS rate (%)"><Input type="number" step="0.01" data-testid="payment-tds-rate-input" value={form.tds_rate} onChange={(e) => setForm({ ...form, tds_rate: e.target.value })} /></Field>
                        <Field label="TDS certificate no."><Input value={form.tds_certificate_no} onChange={(e) => setForm({ ...form, tds_certificate_no: e.target.value })} /></Field>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-sm p-2 text-xs font-mono-num space-y-0.5" data-testid="tds-preview">
                        <div className="flex justify-between"><span>Gross applied to invoice</span><span>{fmtMoney(Number(form.amount || 0))}</span></div>
                        <div className="flex justify-between text-red-700"><span>− TDS deducted</span><span>{fmtMoney(tdsAmount)}</span></div>
                        <div className="flex justify-between font-bold text-emerald-800 border-t border-amber-300 pt-0.5"><span>Net bank receipt</span><span data-testid="tds-net-preview">{fmtMoney(netAmount)}</span></div>
                      </div>
                    </>
                  )}
                </div>

                <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                <Button onClick={submit} data-testid="save-payment-button" className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">Save Payment</Button>
              </div>
            </SheetContent>
          </Sheet>

      <div className="p-6">
        {rows.length === 0 ? (
          <EmptyState title="No payments recorded" description="Once you receive money, log it here to update invoice balances." />
        ) : (
          <div className="bc-card overflow-hidden" data-testid="payments-table">
            <Table className="bc-list">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>TDS</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">TDS</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="text-[#605E5C]">{fmtDate(p.date)}</TableCell>
                    <TableCell className="font-mono-num">{invMap[p.invoice_id]?.invoice_number || p.invoice_id.slice(0, 8)}</TableCell>
                    <TableCell>{invMap[p.invoice_id]?.client_name || "—"}</TableCell>
                    <TableCell><span className="text-[11px] text-[#605E5C]">{p.method}</span></TableCell>
                    <TableCell className="text-xs">
                      {p.tds_section ? (
                        <span className="text-[#8A6E00] font-semibold">{p.tds_section} @ {p.tds_rate}%</span>
                      ) : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono-num">{fmtMoney(p.amount)}</TableCell>
                    <TableCell className="text-right font-mono-num text-[#A4262C]">{p.tds_amount ? `-${fmtMoney(p.tds_amount)}` : "—"}</TableCell>
                    <TableCell className="text-right font-mono-num font-semibold text-[#107C10]">{fmtMoney(p.net_amount || p.amount)}</TableCell>
                    <TableCell><button onClick={() => del(p.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={14} /></button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
